import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { INLINES, BLOCKS, MARKS } from "@contentful/rich-text-types";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { Link } from "gatsby";
import { Button } from "react-bootstrap";

import LazyVideoEmbed from "./LazyVideoEmbed";
import ContentForm from "./ContentForm";

const defaultRenderers = {
    marks: {
        [MARKS.BOLD]: text => <strong>{text}</strong>,
    },
    blocks: {
        [INLINES.HYPERLINK]: (node, children) => {
            const { uri } = node.data;
            return (
                <a href={uri} target="_blank" rel="noopener noreferrer">
                    {children}
                </a>
            );
        },
        [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
        [BLOCKS.QUOTE]: (node, children) => (
            <blockquote className="blockquote">{children}</blockquote>
        ),
        [BLOCKS.EMBEDDED_ASSET]: node => (
            <img
                src={node.data.target.file.url}
                alt={
                    node.data.target.description ||
                    node.data.target.title ||
                    node.data.target.file.url
                }
                className="w-100 rounded"
            />
        ),
        [BLOCKS.TABLE]: (node, children) => (
            <div className="table-responsive">
                <table className="table">
                    <tbody>{children}</tbody>
                </table>
            </div>
        ),
        [BLOCKS.TABLE_CELL]: (node, children) => <td>{children}</td>,
        [BLOCKS.TABLE_HEADER_CELL]: (node, children) => <th>{children}</th>,
        [BLOCKS.EMBEDDED_ENTRY]: node => {
            switch (node?.data?.target?.__typename) {
                case "ContentfulFrame":
                    const frame = node?.data?.target;
                    return (
                        <>
                            <div
                                className={classNames("ratio ratio-16x9 my-3", {
                                    "d-none d-md-block":
                                        frame.mobileImage || frame.hide,
                                })}
                            >
                                <iframe
                                    className="rounded"
                                    title={frame.title}
                                    allowFullScreen
                                    seamless
                                    src={frame.url}
                                    width={1000}
                                    height={500}
                                />
                            </div>
                            {frame.mobileImage && !frame.hide && (
                                <img
                                    src={frame.mobileImage.file.url}
                                    className="d-sm-none rounded"
                                    alt={
                                        frame.mobileImage.description ||
                                        frame.mobileImage.title ||
                                        frame.mobileImage.file.url
                                    }
                                />
                            )}
                        </>
                    );
                case "ContentfulButton":
                    const internal = /^\/(?!\/)/.test(node.data.target.url);
                    if (internal) {
                        return (
                            <Button
                                as={Link}
                                size="lg"
                                to={node.data.target.url}
                                className="button mt-3"
                                variant={node.data.target.color}
                            >
                                {node.data.target.label}
                            </Button>
                        );
                    }
                    return (
                        <Button
                            href={node.data.target.url}
                            target={
                                node.data.target.openNewTab ? "_blank" : "_self"
                            }
                            size="lg"
                            className="button mt-3"
                            variant={node.data.target.color}
                        >
                            {node.data.target.label}
                        </Button>
                    );

                case "ContentfulJumperLink":
                    const jumperLink = node.data.target;

                    return (
                        <h3 id={jumperLink.anchorLink.substring(1)}>
                            {jumperLink.title}
                        </h3>
                    );

                case "ContentfulSingleVideo":
                    const video = node.data.target;

                    return (
                        <div className="mb-4">
                            <LazyVideoEmbed
                                url={video.url}
                                title={video.title}
                            />
                        </div>
                    );

                case "ContentfulForm":
                    const form = node.data.target;

                    return <ContentForm {...form} />;

                default:
                    return null;
            }
        },
    },
};

const RichTextBlock = ({ content, renderers, truncate = 0 }) => {
    const mergedRenderers = Object.assign({}, defaultRenderers, renderers);
    const options = {
        renderMark: mergedRenderers.marks,
        renderNode: mergedRenderers.blocks,
    };

    const children = renderRichText(content, options);

    if (truncate > 0) {
        // Filter out images, as truncate is intended to return only text nodes
        return (
            <>
                {React.Children.toArray(children)
                    .filter(c => c.type !== "img")
                    .slice(0, truncate)}
            </>
        );
    }

    return <>{children}</>;
};

RichTextBlock.propTypes = {
    content: PropTypes.shape({ raw: PropTypes.string }).isRequired,
    renderers: PropTypes.object,
    truncate: PropTypes.number,
};

export default RichTextBlock;
export { BLOCKS, MARKS };
